import { useMutation } from '@apollo/client/react'
import {
  Alert,
  Box,
  Button,
  Center,
  Field,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputProps,
  Link,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { LoginTokenSentAlert } from '@src/components/sco/Account/UserLoginForm'
import {
  SocialLoginButtons,
  SocialLoginButtonsProps,
} from '@src/components/sco/Account/UserLoginForm/SocialLoginButtons'
import { GenericFormProps, processSubmit } from '@src/components/sco/Form/formHelpers'
import { FormContainer } from '@src/components/sco/FormContainer'
import { ChevronRightIcon } from '@src/components/sco/icons'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { OrDivider } from '@src/components/sco/utilities/OrDivider'
import { RegisterUserInput, RegisterUserPayload } from '@src/graphql-types'
import { useCurrentUrl } from '@src/hooks/useCurrentUrl'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { TrackEvent } from '@src/lib/TrackEvent'
import { gql } from '@src/lib/gql'
import { startupsUrl } from '@src/lib/startups-url'
import { localStore } from '@src/lib/store'
import tracker from '@src/lib/tracker'
import { omit } from 'lodash'
import { usePathname, useRouter } from 'next/navigation'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { HiEye, HiEyeOff } from 'react-icons/hi'
import { LuArrowRight } from 'react-icons/lu'

type UserSignupRegisterFormFields = {
  activeSprintSlug?: string
  email?: string
  fullName?: string
  password?: string
  phone?: string
}

export type UserSignupRegisterFormProps = GenericFormProps<UserSignupRegisterFormFields> & {
  defaultValues?: UserSignupRegisterFormFields
  variant?: string
  showTermsOfService?: boolean
  hideAlreadyLoggedInAlert?: boolean
  hideFullName?: boolean
  hideSocialLogin?: boolean
  hubspotFormKey?: string
  onLoginClick?: () => void
  renderLogin?: () => ReactNode
  showPhoneField?: boolean
  signupRequestId?: string | null
  socialLoginButtonsProps?: SocialLoginButtonsProps
  title?: ReactNode
  focusTrigger?: any
  nameField?: {
    autoFocus?: boolean
    label?: string
    required?: boolean
    placeholder?: string
  }
  emailField?: {
    autoFocus?: boolean
    label?: string
    required?: boolean
    placeholder?: string
  }
  afterLoginTokenSent?: (values: UserSignupRegisterFormFields, result: RegisterUserPayload) => Promise<void>
}

export const UserSignupRegisterForm = (props: UserSignupRegisterFormProps) => {
  const { getBreakpointValue } = useBreakpointContext()
  const router = useRouter()
  const pathname = usePathname()
  const currentFullUrl = useCurrentUrl()
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false)
  const [formErrorMessage, setFormErrorMessage] = useState<string>(null)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [requirePassword, setRequirePassword] = useState<boolean>(false)
  const [loginTokenSent, setLoginTokenSent] = useState<boolean>(false)
  const { currentUser, refetchCurrentUser } = useCurrentUser()
  const nameFieldRef = React.useRef<HTMLLabelElement>(null)
  const emailFieldRef = React.useRef<HTMLLabelElement>(null)
  const form = useForm<UserSignupRegisterFormFields>({
    defaultValues: {
      fullName: currentUser?.fullName,
      email: currentUser?.email,
      ...props.defaultValues,
    },
    mode: 'onSubmit',
  })

  // Prefill email from chat / URL after mount (defaultValues only apply once)
  useEffect(() => {
    const email = props.defaultValues?.email
    if (email && !form.getValues('email')) {
      form.setValue('email', email, { shouldDirty: false, shouldValidate: false })
    }
  }, [props.defaultValues?.email, form])

  const [registerUser, { loading, error }] = useMutation(gql`
    mutation ($input: RegisterUserInput!) {
      registerUser(input: $input) {
        loginTokenSent
        viewer {
          id
          currentUser {
            id
            isRegistered
            isGuest
          }
        }
      }
    }
  `)

  const handleSubmit = useCallback(
    async (values: UserSignupRegisterFormFields) => {
      const signupRequestId =
        props.signupRequestId !== undefined ? props.signupRequestId : localStore.get('sco:signupRequestId')
      const input: RegisterUserInput = {
        ...values,
        sendConfirmationRequest: true,
        hubspotFormKey: props.hubspotFormKey,
        signupRequestId,
        returnToUrl: props.returnToHref
          ? startupsUrl(props.returnToHref || pathname || '/account')
          : currentFullUrl,
      }

      return processSubmit({
        props,
        values,
        setFormErrorMessage,
        action: async (values: UserSignupRegisterFormFields) => {
          const result = await registerUser({
            variables: {
              input,
            },
          })

          return result.data?.registerUser
        },
        afterSuccess: async (values: UserSignupRegisterFormFields, result: RegisterUserPayload) => {
          console.log({ result })
          if (result.loginTokenSent) {
            if (props.afterLoginTokenSent) {
              await props.afterLoginTokenSent(values, result)
            }
            setLoginTokenSent(true)
          } else {
            const traits = omit(values, ['password'])
            await tracker.emailCaptured({ ...traits })
            await tracker.s1Submitted({ ...traits })
            await tracker.accountRegistered({
              ...traits,
            })

            if (props.returnToHref) {
              setIsRedirecting(true)
              await refetchCurrentUser()
              if (props.afterSuccess) {
                await props.afterSuccess(values, result)
              }
              router.push(props.returnToHref)
            } else {
              await refetchCurrentUser()
              if (props.afterSuccess) {
                await props.afterSuccess(values, result)
              }
            }
          }
        },
      })
    },
    [pathname, props]
  )

  useEffect(() => {
    if (props.focusTrigger) {
      if (nameFieldRef.current) {
        nameFieldRef.current?.focus()
      } else if (emailFieldRef.current) {
        emailFieldRef.current?.focus()
      }
    }
  }, [props.focusTrigger])

  if (loginTokenSent) {
    return (
      <LoginTokenSentAlert
        containerProps={props.containerProps}
        subtitle={
          <>
            That email address is registered to an account.
            <br /> We sent you an email with an auto-login link to continue.
          </>
        }
        onTryAgain={() => setLoginTokenSent(false)}
      />
    )
  }

  return (
    <FormContainer {...props.containerProps}>
      <TrackEvent action={'accountRegistrationStarted'} onlyOnScreen={true} />
      <TrackEvent action={'s1Viewed'} onlyOnScreen={true} />
      {props.title || props.onLoginClick ? (
        <HStack align={'center'} justify={'space-between'} marginBottom={getBreakpointValue([2, 4])}>
          {props.title && <Heading size={getBreakpointValue(['lg', 'xl'])}>{props.title}</Heading>}
          {props.onLoginClick && (
            <Text color={'colorPalette.fg/75'} fontSize={'11px'} textAlign={'right'}>
              {'Have an account?'}{' '}
              <Link
                color={'colorPalette.fg'}
                marginLeft={'2px'}
                variant={'underline'}
                onClick={props.onLoginClick}
              >
                Login
              </Link>
            </Text>
          )}
        </HStack>
      ) : null}
      {isRedirecting && (
        <Center padding={'1em'}>
          <Spinner />
        </Center>
      )}
      {!isRedirecting && (
        <VStack asChild align={'stretch'} gap={6}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            {!props.hideAlreadyLoggedInAlert && currentUser?.isRegistered && currentUser.isPasswordSet && (
              <Alert.Root borderRadius={'md'} status={'warning'}>
                <Alert.Indicator />
                <Box>
                  {"You're already logged in!"}{' '}
                  {props.returnToHref ? (
                    <>
                      <Link asChild variant={'underline'}>
                        <NextLink href={props.returnToHref}>
                          {'Continue to Next Step'}{' '}
                          <Icon asChild>
                            <LuArrowRight />
                          </Icon>
                        </NextLink>
                      </Link>
                    </>
                  ) : (
                    <Link asChild variant={'underline'}>
                      <NextLink href={'/account'} prefetch={false}>
                        View Account
                      </NextLink>
                    </Link>
                  )}
                </Box>
              </Alert.Root>
            )}
            {formErrorMessage && (
              <Alert.Root borderRadius={'md'} status={'error'}>
                <Alert.Indicator marginRight={'1em'} />
                <Box>{formErrorMessage}</Box>
              </Alert.Root>
            )}
            <Input type={'hidden'} {...form.register('activeSprintSlug', {})} />
            {!props.hideFullName && (
              <Field.Root invalid={!!form.formState.errors.fullName}>
                <Field.Label ref={nameFieldRef}>{props.nameField?.label || 'Full Name'}</Field.Label>
                <Input
                  autoFocus={props.nameField?.autoFocus === false ? false : true}
                  placeholder={props.nameField?.placeholder || 'John Doe'}
                  type={'text'}
                  {...(props.inputProps as InputProps)}
                  {...form.register('fullName', {
                    required: 'Your name is required.',
                  })}
                />
                <Field.ErrorText>{form.formState.errors.fullName?.message}</Field.ErrorText>
              </Field.Root>
            )}
            <Field.Root invalid={!!form.formState.errors.email}>
              <Field.Label ref={emailFieldRef}>{props.emailField?.label || 'Email address'}</Field.Label>
              <Input
                autoFocus={props.emailField?.autoFocus}
                placeholder={props.emailField?.placeholder || 'you@email.com'}
                type={'email'}
                {...(props.inputProps as InputProps)}
                {...form.register('email', {
                  required: 'Email is required.',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                  onChange: e => {
                    if (
                      process.env.NEXT_PUBLIC_DEPLOYMENT_ENV !== 'production' &&
                      e.target.value === 'test++'
                    ) {
                      form.setValue(
                        'email',
                        // eslint-disable-next-line react-hooks/purity
                        `test+x${Math.round(Math.random() * 100_000_000_000)}@startups.com`,
                        {
                          shouldValidate: false,
                          shouldDirty: true,
                        }
                      )
                    }
                  },
                })}
              />
              <Field.ErrorText>{form.formState.errors.email?.message}</Field.ErrorText>
            </Field.Root>
            {requirePassword && (
              <Field.Root invalid={!!form.formState.errors.password}>
                <Field.Label>Choose a Password</Field.Label>
                <InputGroup
                  endElement={
                    <IconButton
                      _hover={{ opacity: 1 }}
                      aria-label={showPassword ? 'Hide Password' : 'Show Password'}
                      opacity={'0.5'}
                      size={'sm'}
                      variant={'ghost'}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <HiEye /> : <HiEyeOff />}
                    </IconButton>
                  }
                >
                  <Input
                    placeholder={'Enter a password'}
                    type={showPassword ? 'text' : 'password'}
                    {...(props.inputProps as InputProps)}
                    {...form.register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password should be at least 8 characters.',
                      },
                    })}
                  />
                </InputGroup>

                <Field.ErrorText>{form.formState.errors.password?.message}</Field.ErrorText>
              </Field.Root>
            )}
            {props.showPhoneField && (
              <Field.Root invalid={!!form.formState.errors.phone}>
                <Field.Label>
                  {'Phone Number - '}
                  <small>Include country code if outside U.S.</small>
                </Field.Label>
                <Input
                  placeholder={'555-555-5555'}
                  type={'tel'}
                  {...(props.inputProps as InputProps)}
                  {...form.register('phone')}
                />
                <Field.ErrorText>{form.formState.errors.phone?.message}</Field.ErrorText>
              </Field.Root>
            )}
            <Box paddingTop={'0.5em'}>
              <Button
                size={'lg'}
                type={'submit'}
                variant={'cta'}
                width={'full'}
                {...props.submitButtonProps}
                loading={form.formState.isSubmitting || props.submitButtonProps?.loading}
              >
                {props.submitButtonProps?.children || 'Create Account & Continue'}
                <ChevronRightIcon />
              </Button>

              {!props.hideSocialLogin && (
                <Box paddingY={getBreakpointValue([2, 2, 4])} width={'full'}>
                  <OrDivider marginBottom={getBreakpointValue([1, 1, 2])} />
                  <SocialLoginButtons
                    actionLabel={'Sign Up'}
                    buttonLayout={'google-callout-grid'}
                    hubspotFormKeys={[props.hubspotFormKey || 'scom-registration']}
                    returnToHref={props.returnToHref}
                    {...props.socialLoginButtonsProps}
                  />
                </Box>
              )}
              {props.showTermsOfService !== false && (
                <Box paddingTop={'0.5em'}>
                  <Text color={'colorPalette.fg/50'} fontSize={'11px'} textAlign={'center'}>
                    {'Submission confirms agreement to our'}{' '}
                    <Link asChild color={'colorPalette.fg/50'} variant={'underline'}>
                      <NextLink href={'/legal/terms-of-service'} target={'_blank'}>
                        Terms of Service
                      </NextLink>
                    </Link>{' '}
                    {'and'}{' '}
                    <Link asChild color={'colorPalette.fg/50'} variant={'underline'}>
                      <NextLink href={'/legal/privacy-policy'} target={'_blank'}>
                        Privacy Policy
                      </NextLink>
                    </Link>
                    .
                  </Text>
                </Box>
              )}
            </Box>
          </form>
        </VStack>
      )}
    </FormContainer>
  )
}
